import React from 'react';
import { css } from '@compiled/react';
import { token } from '@atlaskit/tokens';
import { Checkbox } from '@atlaskit/checkbox';
import { FormValues, ChampionPicklistField } from '../../../types/ChampionApplicationFormTypes';

type FieldErrors = {
  partnerType?: string;
};

type Props = {
  values: FormValues;
  updateField: <K extends keyof FormValues>(key: K, value: FormValues[K]) => void;
  errors?: FieldErrors;
  partnerTypeOptions: ChampionPicklistField['picklistValues'];
};

export const WorkPartnerSection: React.FC<Props> = ({ values, updateField, errors, partnerTypeOptions }) => {
  const toggle = (value: string) => {
    const current = values.partnerType;
    const next = current.includes(value)
      ? current.filter((v) => v !== value)
      : [...current, value];
    updateField('partnerType', next);
  };

  const hasError = !!errors?.partnerType;
  const fieldClassName = hasError
    ? 'champion-form-work-partner champion-form-work-partner--error'
    : 'champion-form-work-partner';

  return (
    <div css={sectionBoxStyles}>
      <div css={sectionTitleStyles}>Work &amp; partner information</div>

      <div css={fieldStyles} className={fieldClassName}>
        <label css={[labelStyles, salesforceLabelStyles, labelBoldStyles]}>
          <abbr css={requiredStyles} title="required" aria-hidden="true">
            *
          </abbr>{' '}
          Do you work for a Solution or Marketplace Partner?
        </label>

        <div css={controlTopStyles}>
          {partnerTypeOptions.map((opt) => (
            <Checkbox
              key={opt.value}
              name="partner_type"
              label={opt.label}
              isChecked={values.partnerType.includes(opt.value)}
              onChange={() => toggle(opt.value)}
            />
          ))}
        </div>

        {errors?.partnerType && <div css={errorStyles}>{errors.partnerType}</div>}
      </div>
    </div>
  );
};

const sectionBoxStyles = css({
  marginBottom: '40px',
});

const sectionTitleStyles = css({
  display: 'none',
  fontWeight: 600,
  textTransform: 'uppercase',
  fontSize: '12px',
  letterSpacing: '0.04em',
  marginBottom: token('space.100'),
});

const fieldStyles = css({
  display: 'flex',
  flexDirection: 'column',
});

const labelStyles = css({
  fontWeight: 400,
  fontSize: '0.75rem',
  fontFamily: 'sans-serif',
  color: '#696969',
  marginBottom: '0.125rem',
  paddingRight: '0.5rem',
  paddingTop: '0.25rem',
  display: 'inline-block',
  overflowWrap: 'break-word',
  wordWrap: 'break-word',
  hyphens: 'auto',
  lineHeight: 1.5,
});

// Form label: matches other fields (salesforceLabelStyles)
const salesforceLabelStyles = css({
  color: '#696969',
  fontSize: '0.75rem',
  fontFamily: 'sans-serif',
  fontWeight: 400,
  marginBottom: '0.125rem',
  paddingRight: '0.5rem',
  paddingTop: '0.25rem',
  display: 'inline-block',
  overflowWrap: 'break-word',
  wordWrap: 'break-word',
  hyphens: 'auto',
  lineHeight: 1.5,
});

// This field's label is bold (options remain normal weight)
const labelBoldStyles = css({
  fontWeight: 600,
});

const requiredStyles = css({
  color: 'var(--champion-form-error)',
  textDecoration: 'none',
  marginRight: '0.125rem',
});

const controlTopStyles = css({
  marginTop: token('space.050'),
});

const errorStyles = css({
  color: 'var(--champion-form-error)',
  fontFamily: 'sans-serif',
  fontSize: '0.75rem',
  fontWeight: 400,
  marginTop: token('space.050'),
});
